import type { ReferralSettings } from '../../api/types';

/**
 * Dates in this section are days, not instants — a hire date has no time and no zone. They travel as
 * ISO `yyyy-mm-dd` and are handled here as that string, never as a `Date`.
 *
 * `new Date('2026-05-15')` parses as midnight **UTC**, so formatting it anywhere west of Greenwich
 * renders 14 May. That is not a hypothetical: it would move every hire date in the table back a day
 * for every user in the United States, which is all of them.
 */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** `2026-05-15` → `5/15/2026`, the shape the spreadsheet this replaces used. */
export function formatDate(iso: string | null): string {
  if (iso === null) return '—';

  const parts = ISO_DATE.exec(iso);
  if (!parts) return iso;

  return `${Number(parts[2])}/${Number(parts[3])}/${parts[1]}`;
}

/** Whole dollars where the amount is whole, which every configured payout is. */
export function formatMoney(amount: number): string {
  return amount.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Whole days from one ISO day to another, negative when `to` is the earlier one.
 *
 * Both ends are parsed as midnight **UTC** — the same trick `addDays` uses below — so the subtraction
 * is between two fixed points and cannot be thrown off by a daylight-saving boundary between them,
 * which is what makes a local-midnight version of this return 89 or 91 twice a year.
 */
export function daysBetween(from: string, to: string): number {
  return Math.round(
    (Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000,
  );
}

function addDays(iso: string, days: number): string {
  const shifted = new Date(Date.parse(`${iso}T00:00:00Z`) + days * 86_400_000);

  return shifted.toISOString().slice(0, 10);
}

/**
 * The window a new referral has to be recorded inside, or null when it is switched off.
 *
 * Mirrors the rule the API applies to a create and not to an edit — see `ReferralValidation.ValidateNew`
 * for why an edit is never refused for it.
 */
export function submissionWindow(
  today: string,
  settings: ReferralSettings,
): { earliestHireDate: string; days: number } | null {
  if (settings.submissionWindowDays <= 0) return null;

  return {
    earliestHireDate: addDays(today, -settings.submissionWindowDays),
    days: settings.submissionWindowDays,
  };
}

/**
 * What a hire date produces, shown in the form before it is saved. The same two rules the API
 * applies: probation is the hire date plus the probation length, and the payout is the first run in
 * the payroll calendar on or after it.
 *
 * A preview, never the stored answer — the table always shows what the server worked out, which is
 * what keeps this from quietly drifting away from it.
 *
 * `payoutDate` is null when the calendar does not reach that far, exactly as the API reports it.
 */
export function derivedDates(
  hireDate: string,
  settings: ReferralSettings,
  payoutDates: readonly string[],
): { probationClearedOn: string; payoutDate: string | null } | null {
  if (!ISO_DATE.test(hireDate)) return null;

  const probationClearedOn = addDays(hireDate, settings.probationDays);

  // Plain string comparison: ISO dates sort correctly as text, which is the whole reason the format
  // is used here rather than anything friendlier.
  const payoutDate = payoutDates
    .filter((date) => date >= probationClearedOn)
    .reduce<string | null>((earliest, date) => (earliest === null || date < earliest ? date : earliest), null);

  return { probationClearedOn, payoutDate };
}
